/**
 * Cookie Consent Management
 *
 * Simple consent management for GDPR compliance.
 * Stores consent state in localStorage.
 */

export const CONSENT_KEY = "cookie_consent";
const CONSENT_TIMESTAMP_KEY = "cookie_consent_timestamp";

export type ConsentState = "accepted" | "declined" | null;

/**
 * Get current consent state
 */
export function getConsentState(): ConsentState {
  if (typeof window === "undefined") return null;

  const consent = localStorage.getItem(CONSENT_KEY);
  if (consent === "accepted" || consent === "declined") {
    return consent;
  }
  return null;
}

/**
 * Check if tracking is allowed
 * Opt-out model: tracking is allowed unless explicitly declined
 */
export function hasTrackingConsent(): boolean {
  return getConsentState() !== "declined";
}

/**
 * Google Consent Mode v2 state. Required by Google Ads for EEA/CH traffic;
 * all four signals follow the single banner choice.
 */
export function googleConsentState(granted: boolean) {
  const value = granted ? "granted" : "denied";
  return {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  };
}

/**
 * Store consent choice
 */
export function setConsent(accepted: boolean): void {
  if (typeof window === "undefined") return;

  localStorage.setItem(CONSENT_KEY, accepted ? "accepted" : "declined");
  localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());

  // Google Consent Mode v2: tell already-loaded Google tags about the choice
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", googleConsentState(accepted));
  }

  // Dispatch event for other components to react
  window.dispatchEvent(
    new CustomEvent("consentChanged", { detail: { accepted } })
  );
}

/**
 * Get consent timestamp (for audit purposes)
 */
export function getConsentTimestamp(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(CONSENT_TIMESTAMP_KEY);
}

/**
 * Clear consent (for testing or user request)
 */
export function clearConsent(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(CONSENT_KEY);
  localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
}
